import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Button,
  Layout,
  Table,
  Tooltip,
  Modal,
  notification,
  FloatButton,
} from "antd";
import {
  FaComment,
  FaCommentSlash,
  FaMusic,
  FaVideo,
  FaFileAlt,
} from "react-icons/fa";
import { AiOutlineWarning } from "react-icons/ai";

// Components
import FooterComponent from "../../components/ui/footer";
import HeaderUIComponent from "../../components/ui/header/header.ui";
import SpinnerComponent from "../../components/spinner/spinner.component";
import SmallSpinnerComponent from "../../components/spinner/small-spinner.component";
import FilenameComponent from "../../components/ui/filename.component";
import AudioPlayer from "../../components/audio-player/audio-player.component";
import DraftScriptModal from "../../components/modal/draft-scirpt-modal";
import VideoPlayer from "../../components/video/video-player";
import CommentsComponent from "../../components/comments/comments.component";

// Services
import { files_api } from "../../services/apis";
import { removeUserDetailsFromLocalStorage } from "../../services/save-user";

const SharedFiles = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isPlayingStatus = useSelector((state) => state.playingStatusReducer);

  // State
  const [sharedFiles, setSharedFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [commentData, setCommentData] = useState({});
  const [commentObjId, setCommentObjId] = useState("");
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [tempSelected, setTempSelected] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [checkType, setCheckType] = useState(null);
  const [isCheckCompleted, setIsCheckCompleted] = useState(false);

  // Modal configuration based on role
  const modalConfig = {
    MTT: {
      title: "Confirm Cross Check",
      description: "Are you sure you want to mark as Cross Check?",
      successMessage: "Cross check done successfully",
      checkType: "CROSS_CHECK",
    },
    CIT: {
      title: "Confirm CIT Check",
      description: "Are you sure you want to mark as CIT Check?",
      successMessage: "CIT check done successfully",
      checkType: "CIT",
    },
    Consultant: {
      title: "Confirm Consultant Check",
      description: "Are you sure you want to mark as Consultant Check?",
      successMessage: "Consultant check done successfully",
      checkType: "Consultant",
    },
    Community: {
      title: "Confirm Community Check",
      description: "Are you sure you want to mark as Community Check?",
      successMessage: "Community check done successfully",
      checkType: "COMMUNITY_CHECK",
    },
    Facilitator: {
      title: "Confirm Community Check",
      description: "Are you sure you want to mark as Community Check?",
      successMessage: "Community check done successfully",
      checkType: "COMMUNITY_CHECK",
    },
  };

  const [api, contextHolder] = notification.useNotification();

  useEffect(() => {
    fetchFiles();
    return () => {};
  }, []);

  const showNotification = (type, message) => {
    notification.destroy();
    api[type]({
      message,
      placement: "top",
    });
  };

  const fetchFiles = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${files_api.sharedFiles}/${id}`);

      if (response.data?.file) {
        const { file } = response.data;

        localStorage.setItem("loggedIn", "false");
        localStorage.removeItem("isBackTranslation");
        localStorage.removeItem("checkPageMsg");
        await removeUserDetailsFromLocalStorage();

        const { receiver } = file;
        localStorage.setItem("email", receiver.email);
        localStorage.setItem("firstName", receiver.firstName);
        localStorage.setItem("lastName", receiver.lastName);
        localStorage.setItem("role", receiver.role);
        localStorage.setItem("id", receiver._id);

        // Set component state
        setUserRole(file.userRole);
        setIsCheckCompleted(file.isCheckCompleted);

        const config = modalConfig[file.userRole] || {};
        setCheckType(config.checkType);

        const checkStatusKey = getCheckStatusKey(file.userRole);

        const checkedFiles = file.files
          .filter((file) => file[checkStatusKey] === true)
          .map((file) => file._id);

        setSelectedFiles(checkedFiles);

        setSharedFiles(
          file.files.map((file) => ({
            ...file,
            key: file._id,
            disabled: file[checkStatusKey] === true,
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching shared files:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getCheckStatusKey = (role) => {
    switch (role) {
      case "MTT":
        return "translatorStatusCheck";
      case "Community":
      case "Facilitator":
        return "communityStatusCheck";
      case "CIT":
        return "citStatusCheck";
      case "Consultant":
        return "consultantStatusCheck";
      default:
        return "translatorStatusCheck";
    }
  };

  const toggleComments = (data, id) => {
    if (showComments && commentObjId === id) {
      setShowComments(false);
    } else {
      setShowComments(true);
      setCommentData(data);
      setCommentObjId(id);
    }
  };

  const handleFileSelection = (selectedRowKeys) => {
    setTempSelected(selectedRowKeys);
    setIsConfirmModalVisible(true);
  };

  const handleConfirmSelection = async () => {
    try {
      setModalLoading(true);
      await axios.post(files_api.checkType, {
        userId: localStorage.getItem("id"),
        checkType: checkType,
        fileId: tempSelected,
      });

      setSelectedFiles(tempSelected);
      setIsConfirmModalVisible(false);

      const config = modalConfig[userRole] || {};
      showNotification("success", config.successMessage);
      checkLinkExpiration();

      fetchFiles()
    } catch (error) {
      console.error("Error confirming selection:", error);
      showNotification(
        "error",
        error.response?.data?.msg || "An error occurred"
      );
    } finally {
      setModalLoading(false);
    }
  };

  const checkLinkExpiration = async () => {
    if (tempSelected.length === sharedFiles.length) {
      try {
        await axios.post(`${files_api.expireSharedLink}/${id}`);
        setTimeout(() => {
          window.location.reload();
        }, 3 * 1000);
      } catch (error) {
        console.error("Error expiring link:", error);
      }
    }
  };

  const redirectToPage = (type) => {
    try {
      let filteredFiles = [];

      switch (type) {
        case "PLAY_ALL":
          filteredFiles = sharedFiles.filter(
            (item) => item.fileType === "audio"
          );
          navigate("/audio-playlist", { state: { audioFiles: filteredFiles } });
          break;
        case "READ_ALL":
          filteredFiles = sharedFiles.filter(
            (item) => item.fileType === "script"
          );
          navigate("/script-view", { state: { scriptFiles: filteredFiles } });
          break;
        case "VIEW_ALL":
          filteredFiles = sharedFiles.filter(
            (item) => item.fileType === "video"
          );
          navigate("/video-player", { state: { videoFiles: filteredFiles } });
          break;
      }
    } catch (error) {
      console.error("Error redirecting:", error);
    }
  };

  const renderFileAction = (record) => {
    return (
      <div className="flex items-center justify-end mr-10">
        {record.fileType === "audio" && (
          <AudioPlayer
            revokeOtherFunctions={null}
            data={record}
            audioType={record.fileType}
            isDisabled={isTableLoading}
            isReverse={false}
            reload={fetchFiles}
          />
        )}

        <p className="flex items-center justify-end mr-10">
          {record.fileType === "script" && (
            <DraftScriptModal data={record} isDisabled={isLoading} />
          )}
        </p>

        <p className="flex items-center justify-end mr-10">
          {record.fileType === "video" && (
            <VideoPlayer data={record} fileType="DRAFTFILE" />
          )}
        </p>

        <Tooltip placement="topLeft" title="Comment">
          <Button
            className="ml-2"
            disabled={isPlayingStatus}
            onClick={() => toggleComments(record, record.fileName)}
          >
            {showComments && commentObjId === record.fileName ? (
              <FaCommentSlash size={16} />
            ) : (
              <FaComment size={16} />
            )}
          </Button>
        </Tooltip>
      </div>
    );
  };

  const columns = [
    {
      title: "File Name",
      dataIndex: "name",
      render: (_, record) => <FilenameComponent file={record} />,
    },
    {
      title: (
        <span className="flex items-center justify-end mr-4-5 w-full">
          Action
        </span>
      ),
      dataIndex: "",
      key: "x",
      render: renderFileAction,
    },
  ];

  const renderExpiredLink = () => (
    <div className="link-expired-container">
      <AiOutlineWarning className="warning-icon" size={48} />
      <h2 className="expired-title">Link Expired</h2>
      <p className="expired-message">
        The link you are trying to access has expired or is no longer available.
      </p>
    </div>
  );

  const renderContent = () => {
    if (isLoading) {
      return <SpinnerComponent />;
    }

    if (isCheckCompleted) {
      return renderExpiredLink();
    }

    return (
      <>
        <h4>Shared Files</h4>
        <Table
          rowSelection={{
            type: "checkbox",
            selectedRowKeys: selectedFiles,
            onChange: handleFileSelection,
            hideSelectAll: true,
            getCheckboxProps: (record) => ({
              disabled: record.disabled,
              hideSelectAll: true,
            }),
          }}
          loading={{
            spinning: isTableLoading,
            indicator: <SmallSpinnerComponent />,
          }}
          pagination={{ pageSize: 5 }}
          columns={columns}
          dataSource={sharedFiles}
        />
        <div className="pb-2 mx-2">
          {showComments && <CommentsComponent chunk={commentData} />}
        </div>
      </>
    );
  };

  return (
    <div className="layout">
      <Layout>
        <Layout.Header>
          <HeaderUIComponent />
        </Layout.Header>
        <Layout>
          <Layout.Content>
            <div className="sharedFilesPage">
              <div className="mt-4">{renderContent()}</div>
            </div>
          </Layout.Content>
        </Layout>
        <Layout.Footer>
          <FooterComponent />
        </Layout.Footer>
      </Layout>

      <Modal
        title={modalConfig[userRole]?.title || "Confirm"}
        open={isConfirmModalVisible}
        onOk={handleConfirmSelection}
        confirmLoading={modalLoading}
        onCancel={() => setIsConfirmModalVisible(false)}
      >
        <p>{modalConfig[userRole]?.description || ""}</p>
      </Modal>

      {contextHolder}

      <FloatButton.Group
        trigger="click"
        type="primary"
        style={{
          insetInlineEnd: 24,
        }}
      >
        <FloatButton
          icon={<FaFileAlt color="white" />}
          tooltip="Read All"
          onClick={() => redirectToPage("READ_ALL")}
        />
        <FloatButton
          icon={<FaMusic color="white" />}
          tooltip="Play All"
          style={{ color: "white" }}
          onClick={() => redirectToPage("PLAY_ALL")}
        />
        <FloatButton
          icon={<FaVideo color="white" />}
          tooltip="View All"
          onClick={() => redirectToPage("VIEW_ALL")}
        />
      </FloatButton.Group>
    </div>
  );
};

export default SharedFiles;
